import { serializer } from '@wix/metro-runtime/ambassador';
import { resolveUrl } from '@wix/metro-runtime';
var _address = { geocode: '_addressLocation' };
var _addressLocation = {
    latitude: 'google.protobuf.DoubleValue',
    longitude: 'google.protobuf.DoubleValue',
};
var _booking = {
    createdDate: 'google.protobuf.Timestamp',
    startDate: 'google.protobuf.Timestamp',
    endDate: 'google.protobuf.Timestamp',
    updatedDate: 'google.protobuf.Timestamp',
    canceledDate: 'google.protobuf.Timestamp',
    contactDetails: '_contactDetails',
};
var _bookingLineItem = {
    pricePerParticipant: 'google.protobuf.DoubleValue',
    serviceChoices: '_pricingServiceChoices',
};
var _calculatePriceRequest = { booking: '_booking' };
var _calculatePriceResponse = { priceInfo: '_priceInfo' };
var _contactDetails = { fullAddress: '_address' };
var _previewPriceRequest = { bookingLineItems: '_bookingLineItem' };
var _previewPriceResponse = { priceInfo: '_priceInfo' };
var _priceInfo = {
    calculatedPrice: 'DOUBLE',
    deposit: 'google.protobuf.DoubleValue',
    bookingLineItems: '_bookingLineItem',
};
var _pricingServiceChoices = {
    pricePerParticipant: 'google.protobuf.DoubleValue',
};
function resolveComWixpressBookingsPricingBookingsPricingServiceUrl(opts) {
    var domainToMappings = {
        'manage._base_domain_': [
            {
                srcPath: '/_api/bookings-pricing',
                destPath: '',
            },
            {
                srcPath: '/_api/bookings-pricing/v2/price',
                destPath: '/v2/pricing',
            },
        ],
        _: [
            {
                srcPath: '/_api/bookings-pricing',
                destPath: '',
            },
            {
                srcPath: '/_api/bookings-pricing/v2/price',
                destPath: '/v2/pricing',
            },
        ],
        'www.wixapis.com': [
            {
                srcPath: '/bookings/v2/pricing/v2/pricing',
                destPath: '/v2/pricing',
            },
            {
                srcPath: '/bookings/v2/pricing',
                destPath: '/v2/pricing',
            },
        ],
    };
    return resolveUrl(Object.assign(opts, { domainToMappings: domainToMappings }));
}
/**
 * Previews the base price for a set of line items belonging to the same
 * service, before a booking is created. During the booking flow, additional
 * taxes and fees may be added to the base price.
 *
 *
 * ## Response
 *
 * The response includes each line item's individual preview price and the
 * total of all line item preview prices. Note that the final price upon
 * booking creation may differ from the preview price.
 *
 * ## Errors
 *
 * _Preview Price_ fails if:
 *
 * - You specify line items that belong to different *services*
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction)).
 * - The site is using the *Bookings Pricing Integration SPI*
 * ([REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction)).
 *
 * ## When to call Calculate Price instead
 *
 * To retrieve the price of an existing booking, call *Calculate Price*
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/pricing/calculate-price) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/calculate-price)).
 */
export function previewPrice(payload) {
    var _a = serializer(_previewPriceRequest, { _bookingLineItem: _bookingLineItem, _pricingServiceChoices: _pricingServiceChoices }), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_previewPriceResponse, {
        _bookingLineItem: _bookingLineItem,
        _priceInfo: _priceInfo,
        _pricingServiceChoices: _pricingServiceChoices,
    }).fromJSON;
    function __previewPrice(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.v2.price_info',
            method: 'POST',
            methodFqn: 'com.wixpress.bookings.pricing.BookingsPricingService.PreviewPrice',
            url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
                protoPath: '/v2/pricing/preview',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __previewPrice.fromReq = fromReq;
    __previewPrice.__isAmbassador = true;
    return __previewPrice;
}
/**
 * Calculates the base price of a booking.
 *
 *
 * The returned price serves as the foundation for charging the customer. During the
 * _eCommerce checkout_
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/ecom/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction)),
 * additional taxes and fees may be added to this base price.
 *
 * ## Price calculation method
 *
 * By default, Wix Bookings calculates a booking's price based on the relevant
 * `serviceOptionsAndVariants.variants.values.price`
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/service-options-and-variants/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction)).
 * You must then specify either `booking.bookedEntity.slot.serviceId` or
 * `booking.bookedEntity.schedule.scheduleId`.
 *
 * If the business uses the *Wix Bookings Pricing Integration service plugin*
 * ([REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction)),
 * the returned `priceInfo` object reflects values received from the SPI implementor in
 * _Calculate Price_
 * ([REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/calculate-price)).
 * In this case, it suffices to specify `booking.bookedEntity`.
 *
 * ## When to call Preview Price instead
 *
 * To estimate the price for multiple booking line items before the booking exists,
 * call *Preview Price*
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/bookings/pricing/preview-price) | [REST](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/preview-price)).
 */
export function calculatePrice(payload) {
    var _a = serializer(_calculatePriceRequest, { _address: _address, _addressLocation: _addressLocation, _booking: _booking, _contactDetails: _contactDetails }), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_calculatePriceResponse, {
        _bookingLineItem: _bookingLineItem,
        _priceInfo: _priceInfo,
        _pricingServiceChoices: _pricingServiceChoices,
    }).fromJSON;
    function __calculatePrice(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.v2.price_info',
            method: 'POST',
            methodFqn: 'com.wixpress.bookings.pricing.BookingsPricingService.CalculatePrice',
            url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
                protoPath: '/v2/pricing/calculate',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __calculatePrice.fromReq = fromReq;
    __calculatePrice.__isAmbassador = true;
    return __calculatePrice;
}
//# sourceMappingURL=http.impl.js.map